/**
 * Canales de soporte de RiskMann: las líneas de WhatsApp, el correo y el horario viven solo aquí. Para
 * cambiarlos, se cambian en este archivo y se despliega Hosting: el pie de página, Soporte técnico, los
 * avisos de registro y de perfil y el botón de WhatsApp del Resumen del en vivo los toman de aquí.
 *
 * La Política de privacidad (TextoPoliticaPrivacidad.vue) nombra el correo como canal oficial de
 * Habeas Data. Es un texto legal que aceptan los usuarios, así que se cambia aparte y a propósito.
 */
import { esFestivoColombia } from "@/utils/festivosColombia";

/** Con el indicativo del país (57) y sin "+" ni espacios, como lo pide wa.me. */
const NUMERO_SOPORTE_TECNICO = "573138688692";
const NUMERO_COMERCIAL = "573123336350";

/** Como se muestra en pantalla: "+57 313 868 8692". */
export const telefonoVisible = (numero) =>
  `+${numero.slice(0, 2)} ${numero.slice(2, 5)} ${numero.slice(5, 8)} ${numero.slice(8)}`;

/** La línea principal es la de soporte técnico: la abren los botones rápidos y los avisos. */
export const SOPORTE_TELEFONO = NUMERO_SOPORTE_TECNICO;

export const SOPORTE_TELEFONO_VISIBLE = telefonoVisible(SOPORTE_TELEFONO);

export const SOPORTE_CORREO = "riskmann@sofu.com.co";

export const ENLACE_CORREO_SOPORTE = `mailto:${SOPORTE_CORREO}`;

const MENSAJE_SOPORTE = "Hola, buen día. Necesito soporte técnico en RiskMann.";

/**
 * Chat de WhatsApp con el mensaje ya escrito.
 * @param {string} numero - Como lo pide wa.me (ver arriba).
 * @param {string} [mensaje]
 * @returns {string}
 */
export function enlaceWhatsapp(numero, mensaje = MENSAJE_SOPORTE) {
  return `https://wa.me/${numero}?text=${encodeURIComponent(mensaje)}`;
}

/**
 * Chat de soporte técnico por WhatsApp con el mensaje ya escrito.
 * @param {string} [mensaje] - Sin mensaje, el saludo general de soporte.
 * @returns {string}
 */
export function enlaceWhatsappSoporte(mensaje = MENSAJE_SOPORTE) {
  return enlaceWhatsapp(NUMERO_SOPORTE_TECNICO, mensaje);
}

/**
 * Chat de la línea comercial (planes, pagos y capacitaciones) por WhatsApp con el mensaje ya escrito.
 * @param {string} mensaje
 * @returns {string}
 */
export function enlaceWhatsappComercial(mensaje) {
  return enlaceWhatsapp(NUMERO_COMERCIAL, mensaje);
}

/** Las dos líneas, en el orden en que se muestran. La comercial también apoya a soporte técnico. */
export const LINEAS_WHATSAPP = [
  {
    id: "tecnico",
    nombre: "Soporte técnico",
    descripcion: "Ayuda para usar RiskMann: acceso a tu cuenta, registros, documentos e informes.",
    principal: true,
    telefono: telefonoVisible(NUMERO_SOPORTE_TECNICO),
    enlace: enlaceWhatsapp(NUMERO_SOPORTE_TECNICO),
  },
  {
    id: "comercial",
    nombre: "Comercial",
    descripcion: "Planes, pagos y capacitaciones. También te ayuda con soporte si la línea técnica está ocupada.",
    principal: false,
    telefono: telefonoVisible(NUMERO_COMERCIAL),
    enlace: enlaceWhatsappComercial("Hola, buen día. Necesito ayuda con RiskMann."),
  },
];

// Horario de atención en hora de Colombia. Cada turno es [inicio, fin], en minutos desde la medianoche.
const minutos = (hora, minuto) => hora * 60 + minuto;
const MANANA = [minutos(8, 15), minutos(11, 45)];
const TARDE = [minutos(14, 15), minutos(17, 45)];
const SABADO = [minutos(9, 15), minutos(11, 45)];
const TURNOS_POR_DIA = [[], [MANANA, TARDE], [MANANA, TARDE], [MANANA, TARDE], [MANANA, TARDE], [MANANA, TARDE], [SABADO]];

/** El horario como se muestra: una fila por grupo de días. */
export const FILAS_HORARIO = [
  { dias: "Lunes a viernes", turnos: [MANANA, TARDE] },
  { dias: "Sábados", turnos: [SABADO] },
  { dias: "Domingos y festivos", turnos: [] },
];

/** «8:15 a. m.» */
export function horaVisible(minutosDelDia) {
  const hora = Math.floor(minutosDelDia / 60);
  const minuto = String(minutosDelDia % 60).padStart(2, "0");
  return `${hora % 12 === 0 ? 12 : hora % 12}:${minuto} ${hora < 12 ? "a. m." : "p. m."}`;
}

/** «8:15 a. m. a 11:45 a. m.» */
export const turnoVisible = ([inicio, fin]) => `${horaVisible(inicio)} a ${horaVisible(fin)}`;

const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MS_DIA = 24 * 60 * 60 * 1000;
// Colombia no cambia la hora durante el año: siempre está en UTC-5
const DESFASE_COLOMBIA_MS = -5 * 60 * 60 * 1000;

/**
 * Si soporte está atendiendo y, si no, cuándo responde: según la hora de Colombia, sin domingos ni festivos.
 * @param {Date} [ahora]
 * @returns {{abierto: boolean, texto: string}}
 */
export function estadoAtencion(ahora = new Date()) {
  // Fecha y hora de Colombia en los campos UTC
  const colombia = new Date(ahora.getTime() + DESFASE_COLOMBIA_MS);
  const minutoActual = colombia.getUTCHours() * 60 + colombia.getUTCMinutes();
  const hoyEsFestivo = esFestivoColombia(colombia.toISOString().slice(0, 10));
  const turnosHoy = hoyEsFestivo ? [] : TURNOS_POR_DIA[colombia.getUTCDay()];
  if (turnosHoy.some(([inicio, fin]) => minutoActual >= inicio && minutoActual < fin)) {
    return { abierto: true, texto: "Estamos atendiendo ahora" };
  }
  // El próximo turno: hoy más tarde o el siguiente día hábil (15 días alcanzan aun con los puentes)
  for (let dias = 0; dias < 15; dias++) {
    const dia = new Date(colombia.getTime() + dias * MS_DIA);
    if (esFestivoColombia(dia.toISOString().slice(0, 10))) continue;
    const turno = TURNOS_POR_DIA[dia.getUTCDay()].find(([inicio]) => dias > 0 || inicio > minutoActual);
    if (!turno) continue;
    const cuando = dias === 0 ? "hoy" : dias === 1 ? "mañana" : `el ${DIAS[dia.getUTCDay()]}`;
    return {
      abierto: false,
      texto: `${hoyEsFestivo ? "Hoy es festivo" : "Fuera de horario"}: te respondemos ${cuando} desde las ${horaVisible(turno[0])}`,
    };
  }
  return { abierto: false, texto: "Fuera de horario" };
}
